"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery, type QueryFunction, type QueryKey } from "@tanstack/react-query";
import Image from "next/image";
import { useState } from "react";
import { Skeleton } from "@domainstack/ui/skeleton";
import { cn } from "@domainstack/ui/utils";
import { simpleHash } from "@domainstack/utils/simple-hash";
type IconQueryOptions = {
  queryKey: QueryKey;
  queryFn?: (...args: never[]) => unknown;
};
type IconQueryResult = {
  success: boolean;
  data: {
    url: string | null;
  } | null;
};
export type RemoteIconProps = {
  /**
   * TanStack Query options from tRPC's queryOptions() method.
   * Expected to resolve to a result with { data: { url: string | null } }.
   */
  queryOptions: IconQueryOptions;
  /** Identifier for fallback avatar (e.g., domain name, provider name) */
  fallbackIdentifier: string;
  /** Size in pixels (default: 16) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Alt text for the image */
  alt?: string;
  /** Data attribute for testing/debugging */
  dataAttribute?: string;
};

// Deterministic color palette - vibrant but not overwhelming
// Using actual color values to avoid Tailwind purging issues
const PLACEHOLDER_COLORS = ["#3b82f6",
// blue
"#a855f7",
// purple
"#ec4899",
// pink
"#f43f5e",
// rose
"#f97316",
// orange
"#f59e0b",
// amber
"#10b981",
// emerald
"#14b8a6",
// teal
"#06b6d4",
// cyan
"#6366f1",
// indigo
"#8b5cf6",
// violet
"#d946ef" // fuchsia
] as const;
const baseClassName = cn("pointer-events-none size-4 rounded-xs select-none");
function IconSkeleton(t0) {
  const $ = _c(5);
  const {
    className,
    style
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(baseClassName, "bg-input", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== style || $[3] !== t1) {
    t2 = <Skeleton className={t1} style={style} />;
    $[2] = style;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type FallbackIconProps = Omit<RemoteIconProps, "queryOptions">;
function FallbackIcon(t0) {
  const $ = _c(17);
  const {
    size: t1,
    className,
    style,
    fallbackIdentifier,
    alt,
    dataAttribute
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  let t2;
  if ($[0] !== fallbackIdentifier[0]) {
    t2 = fallbackIdentifier[0]?.toUpperCase() || "?";
    $[0] = fallbackIdentifier[0];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const letter = t2;
  const backgroundColor = PLACEHOLDER_COLORS[simpleHash(fallbackIdentifier) % PLACEHOLDER_COLORS.length];
  const fontSize = Math.max(9, Math.floor(size * 0.55));
  let t3;
  if ($[2] !== className) {
    t3 = cn(baseClassName, "flex items-center justify-center font-bold text-white", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== backgroundColor || $[5] !== fontSize || $[6] !== style) {
    t4 = {
      ...style,
      fontSize,
      backgroundColor
    };
    $[4] = backgroundColor;
    $[5] = fontSize;
    $[6] = style;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = alt ?? `${fallbackIdentifier} icon`;
  let t6;
  if ($[8] !== dataAttribute || $[9] !== fallbackIdentifier) {
    t6 = dataAttribute ? {
      [dataAttribute]: fallbackIdentifier
    } : {};
    $[8] = dataAttribute;
    $[9] = fallbackIdentifier;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== letter || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div className={t3} style={t4} role="img" aria-label={t5} {...t6}>{letter}</div>;
    $[11] = letter;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/**
 * Shared component for rendering remote icons (favicons, logos, etc.)
 * with loading states, error handling, and letter avatar fallback.
 */
export function RemoteIcon(t0) {
  const $ = _c(28);
  const {
    size: t1,
    className,
    style,
    queryOptions,
    fallbackIdentifier,
    alt,
    dataAttribute
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  const [failedUrl, setFailedUrl] = useState(null);
  const t2 = queryOptions.queryFn as QueryFunction<IconQueryResult>;
  let t3;
  if ($[0] !== queryOptions.queryKey || $[1] !== t2) {
    t3 = {
      queryKey: queryOptions.queryKey,
      queryFn: t2,
      retry: false,
      retryOnMount: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    };
    $[0] = queryOptions.queryKey;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: result,
    isLoading,
    isError
  } = useQuery(t3);
  if (isLoading) {
    let t4;
    if ($[3] !== className || $[4] !== style) {
      t4 = <IconSkeleton className={className} style={style} />;
      $[3] = className;
      $[4] = style;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  const url = result?.data?.url;
  if (isError || !url || failedUrl === url) {
    let t4;
    if ($[6] !== alt || $[7] !== className || $[8] !== dataAttribute || $[9] !== fallbackIdentifier || $[10] !== size || $[11] !== style) {
      t4 = <FallbackIcon size={size} className={className} style={style} fallbackIdentifier={fallbackIdentifier} alt={alt} dataAttribute={dataAttribute} />;
      $[6] = alt;
      $[7] = className;
      $[8] = dataAttribute;
      $[9] = fallbackIdentifier;
      $[10] = size;
      $[11] = style;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    return t4;
  }
  let t4;
  if ($[13] !== className) {
    t4 = cn(baseClassName, className);
    $[13] = className;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = alt ?? `${fallbackIdentifier} icon`;
  let t6;
  if ($[15] !== url) {
    t6 = () => setFailedUrl(url);
    $[15] = url;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== dataAttribute || $[18] !== fallbackIdentifier) {
    t7 = dataAttribute ? {
      [dataAttribute]: fallbackIdentifier
    } : {};
    $[17] = dataAttribute;
    $[18] = fallbackIdentifier;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== size || $[21] !== style || $[22] !== t4 || $[23] !== t5 || $[24] !== t6 || $[25] !== t7 || $[26] !== url) {
    t8 = <Image key={url} src={url} width={size} height={size} className={t4} style={style} unoptimized={true} priority={false} draggable={false} alt={t5} onError={t6} {...t7} />;
    $[20] = size;
    $[21] = style;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
    $[26] = url;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}